import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, InputAdornment, Chip, IconButton, Tooltip, CircularProgress, Alert, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControl, FormLabel, FormControlLabel,
  ToggleButtonGroup, ToggleButton, Autocomplete, Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { adminClient } from '../data/adminClient';
import TestSchoolChip from '../TestSchoolChip';
import {
  participationFeeOwed, schoolRegFeeOwed, schoolEntryFeeOwed, staffMemberFeeOwed, paymentStatus,
  paidForEntry, paidForEntryAndParticipant, paidForParticipant, unattributedEntryPayments, creditFor,
  schoolCreditBalance,
} from './paymentCalculations';
import { formatCurrency, entryRateFor, entryFeeTotalForDancer } from '../../teacher/utils/feeCalculations';

// Puts the user-entered note first and appends whatever this payment would
// have auto-generated as a note on its own (e.g. a participant name, an
// entry title), so nothing already being recorded gets silently dropped.
const combineNotes = (userNote, autoNote) => [userNote, autoNote].map(n => (n || '').trim()).filter(Boolean).join(' — ');

// A plain amount + payer-name pair, click-to-edit — reused for School
// Registration Fee, each participant's Participation Fee, and each staff
// member's Extra Fee. "Mark fully paid" just pre-fills the owed value
// through the same save handler as a manual entry — no divergent code path.
function FeeAmountEditor({ amountPaid, paidBy, owed, onSave }) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(String(amountPaid || 0));
  const [payer, setPayer] = useState(paidBy || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!editing) {
      setAmount(String(amountPaid || 0));
      setPayer(paidBy || '');
    }
  }, [amountPaid, paidBy, editing]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(Number(amount) || 0, payer);
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2">
          {formatCurrency(amountPaid || 0)}{paidBy ? ` — ${paidBy}` : ''}
        </Typography>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => setEditing(true)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size="small" type="number" label="Amount" value={amount}
          onChange={e => setAmount(e.target.value)} sx={{ width: 100 }} />
        <TextField size="small" label="Paid by" value={payer}
          onChange={e => setPayer(e.target.value)} sx={{ width: 140 }} />
        <Tooltip title={`Mark fully paid (${formatCurrency(owed)})`}>
          <IconButton size="small" onClick={() => setAmount(String(owed))} disabled={saving}>
            <PaidIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Save">
          <span>
            <IconButton size="small" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Cancel">
          <IconButton size="small" onClick={() => setEditing(false)} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}
    </Box>
  );
}

function StatusChip({ amountPaid, amountOwed }) {
  const { label, tone } = paymentStatus(amountPaid, amountOwed);
  return <Chip label={label} color={tone} size="small" variant={tone === 'success' ? 'filled' : 'outlined'} />;
}

// Opens the matching admin invoice PDF — the exact same document, with the
// same persisted invoice number, the teacher's own copy uses (see backend's
// getOrCreateInvoiceNumber). Tooltip surfaces that number plus when the
// teacher last generated their own copy, if ever, so the secretary can
// compare hers against theirs without a separate confirm step.
function InvoiceButton({ rowIndex, type, invoiceNumber, printedAt }) {
  const printedLabel = printedAt
    ? `Teacher printed: ${new Date(printedAt).toLocaleString('en-CA')}`
    : 'Not yet printed by teacher';
  const title = `${invoiceNumber ? `Invoice ${invoiceNumber}` : 'Generate invoice'} — ${printedLabel}`;
  return (
    <Tooltip title={title}>
      <IconButton size="small" onClick={() => adminClient.openInvoicePdf(rowIndex, type)}>
        <ReceiptLongIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

// Chip pairing with InvoiceButton on the Participation/Entry cards — shows
// whether the teacher has confirmed that category's data is final (locked,
// via lockParticipants()/lockEntries()), a more authoritative "should be no
// more changes" signal than just the printed timestamp (a teacher can
// reprint without re-locking, but can't unlock).
function LockStatusChip({ locked }) {
  return (
    <Chip
      size="small"
      label={locked ? 'Locked — teacher confirmed final' : 'Not locked yet'}
      color={locked ? 'success' : 'default'}
      variant={locked ? 'filled' : 'outlined'}
    />
  );
}

// Shared by the Participation Fees and Staff/Chaperone Fee cards' "Mark all
// unpaid as fully paid" bulk action — prompts once for a single payer name
// (the usual case: one lump-sum cheque covering everyone still owing),
// applied to every currently-unpaid row in that category.
function BulkPaidDialog({ open, label, onClose, onConfirm }) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setName(''); setError(null); } }, [open]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(name);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Mark all unpaid as fully paid</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{label}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField autoFocus fullWidth size="small" label="Paid by" value={name} onChange={e => setName(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting}>
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Fixed application order when more than one category is checked: cover
// Registration first, then Participant Fee (for whichever participants are
// checked), then Entry Fee, then Extra Fee — spilling whatever's left of the
// payment into the next checked category, e.g. a payment that covers one
// participant with money left over automatically flows into entry fees.
const FEE_CATEGORY_ORDER = ['registration', 'participant', 'entry', 'extra'];

// Consolidated entry point at the top of a school's detail panel — lets the
// budget secretary record one payment (e.g. a single cheque) and route it
// across whichever fee categories it actually covers, instead of hunting for
// the right card below and splitting it manually. The per-card "Apply
// payment" controls are unchanged and still work independently. Participant
// Fee payments are split across whichever participants are checked (in
// roster order), the same way the Participation Fees card already splits a
// lump payment across everyone; Extra Fee payments auto-distribute across
// unpaid staff the same way, with no picker since only participant-level
// selection was requested.
// mode 'credit' repurposes this same dialog/distribution logic to apply the
// school's existing credit balance (see schoolCreditBalance) toward whatever
// the admin picks, instead of recording a brand-new incoming payment —
// maxAmount caps the amount field at what's actually available, "Paid by" is
// pre-filled and doesn't need re-entering, and onCreditConsumed is called
// with however much actually got applied so the caller can record the
// matching negative school-credit ledger row.
function ApplyPaymentDialog({
  open, onClose, schoolName, registration, participants, entries, staff, entryPayments, prices,
  onApplyRegistrationFee, onApplyParticipantFee, onApplyEntryFee, onApplyExtraFee,
  mode = 'payment', maxAmount, onCreditConsumed,
}) {
  const isCredit = mode === 'credit';
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [amount, setAmount] = useState('');
  const [paidBy, setPaidBy] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [entryMode, setEntryMode] = useState('lump');
  const [selectedEntryParticipantIds, setSelectedEntryParticipantIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedTypes(new Set());
      setAmount(isCredit ? String(maxAmount || 0) : '');
      setPaidBy(isCredit ? 'School credit' : '');
      setNotes('');
      setSelectedIds(new Set());
      setEntryMode('lump');
      setSelectedEntryParticipantIds(new Set());
      setSubmitting(false);
      setError(null);
      setNote(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const regOwed = schoolRegFeeOwed(prices);
  const regBalance = regOwed - (registration?.schoolRegFeeAmountPaid || 0);

  const participantOwedEach = participationFeeOwed(prices);
  const participantBalance = participants.reduce(
    (sum, p) => sum + Math.max(0, participantOwedEach - (p.participationFeeAmountPaid || 0)), 0
  );

  const entryOwed = schoolEntryFeeOwed(entries, prices);
  const entryBalance = entryOwed - entryPayments.reduce((sum, p) => sum + p.amount, 0);

  // Only dancers who are actually in ≥1 entry can be picked for the "by
  // participant" Entry Fee mode below.
  const entryParticipantIds = new Set(entries.flatMap(e => e.dancerIds || []));
  const entryParticipants = participants.filter(p => entryParticipantIds.has(p.id));

  const extraBalance = staff.reduce(
    (sum, s, i) => sum + Math.max(0, staffMemberFeeOwed(i, prices) - (s.feeAmountPaid || 0)), 0
  );

  const toggleType = (type) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
        if (type === 'participant') setSelectedIds(new Set());
        if (type === 'entry') { setEntryMode('lump'); setSelectedEntryParticipantIds(new Set()); }
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const toggleParticipant = (rowIndex) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex); else next.add(rowIndex);
      return next;
    });
  };

  const allSelected = participants.length > 0 && selectedIds.size === participants.length;

  const toggleEntryParticipant = (id) => {
    setSelectedEntryParticipantIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allEntryParticipantsSelected = entryParticipants.length > 0 && selectedEntryParticipantIds.size === entryParticipants.length;

  const handleApply = async () => {
    const amt = Number(amount);
    if (isNaN(amt) || amt <= 0) { setError('Enter a valid, positive amount.'); return; }
    if (isCredit && amt > (maxAmount || 0) + 0.004) { setError(`Cannot apply more than the available credit (${formatCurrency(maxAmount || 0)}).`); return; }
    if (!paidBy.trim()) { setError('Paid by is required.'); return; }
    if (selectedTypes.size === 0) { setError('Select at least one fee category to apply this payment toward.'); return; }
    if (selectedTypes.has('participant') && selectedIds.size === 0) { setError('Select at least one participant for the Participant Fee category.'); return; }
    if (selectedTypes.has('entry') && entryMode === 'participant' && selectedEntryParticipantIds.size === 0) {
      setError('Select at least one participant for the Entry Fee category, or switch back to Lump sum.'); return;
    }

    setSubmitting(true);
    setError(null);
    setNote(null);
    try {
      let remaining = amt;
      for (const type of FEE_CATEGORY_ORDER) {
        if (remaining <= 0 || !selectedTypes.has(type)) continue;
        if (type === 'registration') {
          const applied = Math.min(remaining, Math.max(0, regBalance));
          if (applied > 0) {
            await onApplyRegistrationFee(applied, paidBy, notes);
            remaining -= applied;
          }
        } else if (type === 'participant') {
          const selected = participants.filter(p => selectedIds.has(p.rowIndex));
          remaining = await onApplyParticipantFee(remaining, paidBy, selected, notes);
        } else if (type === 'entry') {
          if (entryMode === 'participant') {
            remaining = await onApplyEntryFee(remaining, paidBy, selectedEntryParticipantIds, notes);
          } else {
            const applied = Math.min(remaining, Math.max(0, entryBalance));
            if (applied > 0) {
              await onApplyEntryFee(applied, paidBy, null, notes);
              remaining -= applied;
            }
          }
        } else if (type === 'extra') {
          remaining = await onApplyExtraFee(remaining, paidBy, notes);
        }
      }

      const totalApplied = amt - remaining;
      if (isCredit && totalApplied > 0) await onCreditConsumed(totalApplied);

      if (remaining > 0.004) {
        setNote(`The selected categories are now fully paid — ${formatCurrency(remaining)} of this ${isCredit ? 'credit' : 'payment'} could not be applied.`);
        setAmount(isCredit ? '0' : '');
        setPaidBy(isCredit ? 'School credit' : '');
        setNotes('');
        setSelectedIds(new Set());
        setSelectedEntryParticipantIds(new Set());
      } else {
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const balanceLine = (balance) => (
    <Typography variant="body2" color={balance > 0 ? 'warning.main' : 'success.main'}>{formatCurrency(balance)} due</Typography>
  );

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isCredit ? 'Apply School Credit' : 'Apply Payment'} — {schoolName}</DialogTitle>
      <DialogContent>
        {isCredit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Applying from the school's available credit ({formatCurrency(maxAmount || 0)}) — see the Transactions history for where it came from.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {note && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNote(null)}>{note}</Alert>}

        <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
          <FormLabel component="legend">Apply toward (select one or more)</FormLabel>
          <FormControlLabel disabled={!registration} control={
            <Checkbox size="small" checked={selectedTypes.has('registration')} onChange={() => toggleType('registration')} />
          } label={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <span>School Registration Fee</span>{balanceLine(regBalance)}
            </Box>
          } />
          <FormControlLabel disabled={participants.length === 0} control={
            <Checkbox size="small" checked={selectedTypes.has('participant')} onChange={() => toggleType('participant')} />
          } label={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <span>Participant Fee</span>{balanceLine(participantBalance)}
            </Box>
          } />
          <FormControlLabel disabled={entries.length === 0} control={
            <Checkbox size="small" checked={selectedTypes.has('entry')} onChange={() => toggleType('entry')} />
          } label={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <span>Entry Fee</span>{balanceLine(entryBalance)}
            </Box>
          } />
          <FormControlLabel disabled={staff.length === 0} control={
            <Checkbox size="small" checked={selectedTypes.has('extra')} onChange={() => toggleType('extra')} />
          } label={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <span>Extra Fee (Staff/Chaperone)</span>{balanceLine(extraBalance)}
            </Box>
          } />
        </FormControl>

        {selectedTypes.has('participant') && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Select which participants this payment covers:</Typography>
              <Button size="small" onClick={() => setSelectedIds(allSelected ? new Set() : new Set(participants.map(p => p.rowIndex)))}>
                {allSelected ? 'Deselect all' : 'Select all'}
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Owed</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants.map(p => (
                    <TableRow key={p.rowIndex} hover onClick={() => toggleParticipant(p.rowIndex)} sx={{ cursor: 'pointer' }}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selectedIds.has(p.rowIndex)} onClick={e => e.stopPropagation()} onChange={() => toggleParticipant(p.rowIndex)} />
                      </TableCell>
                      <TableCell>{p.firstName} {p.lastName}</TableCell>
                      <TableCell>{formatCurrency(participantOwedEach)}</TableCell>
                      <TableCell><StatusChip amountPaid={p.participationFeeAmountPaid} amountOwed={participantOwedEach} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {selectedTypes.has('entry') && (
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup size="small" exclusive value={entryMode}
              onChange={(_, v) => v && setEntryMode(v)} sx={{ mb: entryMode === 'participant' ? 1 : 0 }}>
              <ToggleButton value="lump">Lump sum (whole school)</ToggleButton>
              <ToggleButton value="participant">By participant</ToggleButton>
            </ToggleButtonGroup>

            {entryMode === 'participant' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Select which dancers this payment covers:</Typography>
                  <Button size="small" onClick={() => setSelectedEntryParticipantIds(
                    allEntryParticipantsSelected ? new Set() : new Set(entryParticipants.map(p => p.id))
                  )}>
                    {allEntryParticipantsSelected ? 'Deselect all' : 'Select all'}
                  </Button>
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, overflowY: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Entry Fee Balance</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Participation Fee Balance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entryParticipants.map(p => {
                        const entryFeeBalance = entryFeeTotalForDancer(p.id, entries, prices) - paidForParticipant(p.id, entryPayments);
                        const participationBalance = participantOwedEach - (p.participationFeeAmountPaid || 0);
                        return (
                          <TableRow key={p.id} hover onClick={() => toggleEntryParticipant(p.id)} sx={{ cursor: 'pointer' }}>
                            <TableCell padding="checkbox">
                              <Checkbox size="small" checked={selectedEntryParticipantIds.has(p.id)} onClick={e => e.stopPropagation()} onChange={() => toggleEntryParticipant(p.id)} />
                            </TableCell>
                            <TableCell>{p.firstName} {p.lastName}</TableCell>
                            <TableCell>{formatCurrency(Math.max(0, entryFeeBalance))}</TableCell>
                            <TableCell>{formatCurrency(Math.max(0, participationBalance))}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Applied one dancer at a time, in the order selected — each dancer's entries first, then their Participation Fee if
                  anything's left, then recorded as a credit for them if there's still money left after that — before moving to the next dancer.
                </Typography>
              </>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            size="small" type="number" label={isCredit ? 'Amount to apply' : 'Amount received'} value={amount}
            onChange={e => setAmount(e.target.value)} sx={{ width: 150 }}
            helperText={isCredit ? `Available: ${formatCurrency(maxAmount || 0)}` : undefined}
          />
          <TextField size="small" required label="Paid by" value={paidBy}
            onChange={e => setPaidBy(e.target.value)} sx={{ width: 200 }} disabled={isCredit} />
        </Box>
        <TextField size="small" label="Notes" value={notes} onChange={e => setNotes(e.target.value)}
          multiline minRows={2} fullWidth sx={{ mt: 1.5 }} />
        {selectedTypes.size > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Applied to the checked categories in the order listed above, fully covering each before spilling over into the next.
          </Typography>
        )}
        {selectedTypes.has('participant') && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Within Participant Fee: applied to the selected participants in the order shown, fully paying each in turn until the amount runs out.
          </Typography>
        )}
        {selectedTypes.has('extra') && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Within Extra Fee: applied across unpaid staff/chaperones in roster order, fully paying each in turn until the amount runs out.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" startIcon={<PaidIcon />} onClick={handleApply} disabled={submitting}>
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Apply payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const TRANSACTION_CATEGORY_LABELS = {
  registration: 'School Registration Fee',
  participant: 'Participant Fee',
  entry: 'Entry Fee',
  extra: 'Extra Fee (Staff/Chaperone)',
  credit: 'Credit',
  'school-credit': 'School Credit',
};

// Read-only, chronological view across all four fee categories — merges the
// per-category cumulative-field payments (Registration/Participant/Extra,
// logged as a supplementary audit trail — see logFeePayment) with Entry
// Fees' own ledger (its actual source of truth for "amount paid"), since
// both now live in the same "Fee Payments" tab. Deliberately no delete here
// — that stays on the Entry Fees card's own table below, the one place a
// delete actually changes a balance; offering it here too would misleadingly
// imply the same for the other three categories.
function TransactionsDialog({ open, onClose, schoolName, payments }) {
  const sorted = [...payments].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const handleExportCSV = () => {
    const headers = ['Date', 'Category', 'Amount', 'Paid By', 'Note'];
    const data = sorted.map(p => [
      p.timestamp ? new Date(p.timestamp).toLocaleString('en-CA') : '',
      TRANSACTION_CATEGORY_LABELS[p.category] || p.category,
      p.amount.toFixed(2),
      p.paidBy || '',
      p.note || '',
    ]);
    const csv = [headers, ...data]
      .map(row => row.map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${schoolName || 'school'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Transactions — {schoolName}</DialogTitle>
      <DialogContent>
        {sorted.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No transactions recorded yet.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Paid By</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map(p => (
                  <TableRow key={p.rowIndex} hover>
                    <TableCell>{p.timestamp ? new Date(p.timestamp).toLocaleString('en-CA') : '—'}</TableCell>
                    <TableCell>{TRANSACTION_CATEGORY_LABELS[p.category] || p.category}</TableCell>
                    <TableCell>{formatCurrency(p.amount)}</TableCell>
                    <TableCell>{p.paidBy || '—'}</TableCell>
                    <TableCell>{p.note || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button startIcon={<DownloadIcon />} onClick={handleExportCSV} disabled={sorted.length === 0} sx={{ mr: 'auto' }}>
          Export CSV
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function SchoolRegFeeCard({ registration, prices, onSave }) {
  const owed = schoolRegFeeOwed(prices);
  const paid = registration?.schoolRegFeeAmountPaid || 0;
  const balance = owed - paid;
  const [savingProceed, setSavingProceed] = useState(false);
  const [proceedError, setProceedError] = useState(null);

  // OK to Proceed: show this school's participants, entries and staff on the
  // admin event pages before the fee is fully paid (see backend
  // getPaymentClearedSchoolNames). Only offered while a balance is owing.
  const toggleOkToProceed = async (checked) => {
    setSavingProceed(true);
    setProceedError(null);
    try {
      await onSave(registration.rowIndex, { okToProceed: checked });
    } catch (err) {
      setProceedError(err.message);
    } finally {
      setSavingProceed(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography fontWeight={600}>School Registration Fee</Typography>
          <StatusChip amountPaid={paid} amountOwed={owed} />
        </Box>

        {!registration ? (
          <Typography variant="body2" color="text.secondary">No event registration record for this school yet.</Typography>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell align="right" sx={{ borderBottom: 'none', pt: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Amount Owed</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: 'none', pt: 1.5, width: 160 }}>
                      <Typography variant="body2" color="text.secondary">{formatCurrency(owed)}</Typography>
                    </TableCell>
                  </TableRow>
                  {paid > 0 && (
                    <TableRow>
                      <TableCell align="right" sx={{ borderBottom: 'none' }}>
                        <Typography variant="body2" color="text.secondary">Payment</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: 'none' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(paid)}{registration.schoolRegFeePaidBy ? ` — ${registration.schoolRegFeePaidBy}` : ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell align="right">
                      <Typography variant="subtitle2" fontWeight={600}>Balance</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2" fontWeight={600} color={balance > 0 ? 'warning.main' : 'success.main'}>
                        {formatCurrency(balance)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {(balance > 0 || registration.okToProceed) && (
              <Box sx={{ mb: 1.5 }}>
                <Tooltip title="Show this school's participants, entries and staff on the event pages before the registration fee is fully paid (e.g. a board paying by PO).">
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!!registration.okToProceed}
                        disabled={savingProceed}
                        onChange={e => toggleOkToProceed(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">OK to Proceed before payment</Typography>}
                  />
                </Tooltip>
                {proceedError && <Alert severity="error" sx={{ mt: 1 }}>{proceedError}</Alert>}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <InvoiceButton
                rowIndex={registration.rowIndex}
                type="school"
                invoiceNumber={registration.schoolFeeInvoiceNumber}
                printedAt={registration.schoolFeeInvoicePrintedAt}
              />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ParticipationFeesCard({ participants, prices, onSaveRow, registration }) {
  const owed = participationFeeOwed(prices);
  const totalOwed = participants.length * owed;
  const totalPaid = participants.reduce((sum, p) => sum + (p.participationFeeAmountPaid || 0), 0);
  const totalOutstanding = totalOwed - totalPaid;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography fontWeight={600} sx={{ mb: 1.5 }}>Participation Fees</Typography>
        {participants.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No participants registered for this school yet.</Typography>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Owed</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount Paid / Paid By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants.map(p => (
                    <TableRow key={p.rowIndex} hover>
                      <TableCell>{p.firstName} {p.lastName}</TableCell>
                      <TableCell>{formatCurrency(owed)}</TableCell>
                      <TableCell>
                        <FeeAmountEditor
                          amountPaid={p.participationFeeAmountPaid}
                          paidBy={p.participationFeePaidBy}
                          owed={owed}
                          onSave={(amount, paidBy) => onSaveRow(p.id, { participationFeeAmountPaid: amount, participationFeePaidBy: paidBy })}
                        />
                      </TableCell>
                      <TableCell><StatusChip amountPaid={p.participationFeeAmountPaid} amountOwed={owed} /></TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} align="right" sx={{ borderBottom: 'none', pt: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Amount Owed</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: 'none', pt: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">{formatCurrency(totalOwed)}</Typography>
                    </TableCell>
                  </TableRow>
                  {totalPaid > 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="right" sx={{ borderBottom: 'none' }}>
                        <Typography variant="body2" color="text.secondary">Payment</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: 'none' }}>
                        <Typography variant="body2" color="text.secondary">{formatCurrency(totalPaid)}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      <Typography variant="subtitle2" fontWeight={600}>Balance</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2" fontWeight={600} color={totalOutstanding > 0 ? 'warning.main' : 'success.main'}>
                        {formatCurrency(totalOutstanding)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {registration && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <LockStatusChip locked={registration.participantsLocked} />
                <InvoiceButton
                  rowIndex={registration.rowIndex}
                  type="participants"
                  invoiceNumber={registration.participantsInvoiceNumber}
                  printedAt={registration.participantsInvoicePrintedAt}
                />
              </Box>
            )}
          </>
        )}

      </CardContent>
    </Card>
  );
}

// Display-only — no payment entry here. All Entry Fee payments (lump or
// by-participant) are recorded through the single top-of-page "Apply
// Payment" button/dialog; this card just shows the resulting balances,
// broken down either by entry or by participant.
function EntryFeesCard({ entries, participants, prices, payments, registration }) {
  const [view, setView] = useState('entry');

  const owed = schoolEntryFeeOwed(entries, prices);
  const paid = payments.reduce((sum, p) => sum + p.amount, 0);
  const unallocated = unattributedEntryPayments(payments);

  const participantIdsInEntries = new Set(entries.flatMap(e => e.dancerIds || []));
  const entryParticipants = participants.filter(p => participantIdsInEntries.has(p.id));

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography fontWeight={600}>Entry Fees</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
              <ToggleButton value="entry">By Entry</ToggleButton>
              <ToggleButton value="participant">By Participant</ToggleButton>
            </ToggleButtonGroup>
            <StatusChip amountPaid={paid} amountOwed={owed} />
          </Box>
        </Box>

        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>No entries registered for this school yet.</Typography>
        ) : view === 'entry' ? (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Entry</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Style</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}># Dancers</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Paid</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(e => {
                  const numDancers = (e.dancerIds || []).length;
                  const total = entryRateFor(e, prices) * numDancers;
                  const entryPaid = paidForEntry(e.title, payments);
                  return (
                    <TableRow key={e.title} hover>
                      <TableCell>{e.title}</TableCell>
                      <TableCell>{e.style || '—'}</TableCell>
                      <TableCell align="right">{numDancers}</TableCell>
                      <TableCell align="right">{formatCurrency(total)}</TableCell>
                      <TableCell align="right">{formatCurrency(entryPaid)}</TableCell>
                      <TableCell align="right">{formatCurrency(total - entryPaid)}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={5} align="right" sx={{ borderBottom: 'none', pt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">Amount Owed</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: 'none', pt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">{formatCurrency(owed)}</Typography>
                  </TableCell>
                </TableRow>
                {paid > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ borderBottom: 'none' }}>
                      <Typography variant="body2" color="text.secondary">Payment</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: 'none' }}>
                      <Typography variant="body2" color="text.secondary">{formatCurrency(paid)}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={5} align="right">
                    <Typography variant="subtitle2" fontWeight={600}>Balance</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight={600} color={owed - paid > 0 ? 'warning.main' : 'success.main'}>
                      {formatCurrency(owed - paid)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}># Entries</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Owed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Paid</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entryParticipants.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>No participants in any entries yet.</TableCell></TableRow>
                ) : entryParticipants.map(p => {
                  const theirEntries = entries.filter(e => (e.dancerIds || []).includes(p.id));
                  const participantOwed = entryFeeTotalForDancer(p.id, entries, prices);
                  const participantPaid = paidForParticipant(p.id, payments);
                  const credit = creditFor(p.id, payments);
                  return (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.firstName} {p.lastName}</TableCell>
                      <TableCell align="right">{theirEntries.length}</TableCell>
                      <TableCell align="right">{formatCurrency(participantOwed)}</TableCell>
                      <TableCell align="right">{formatCurrency(participantPaid)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <StatusChip amountPaid={participantPaid} amountOwed={participantOwed} />
                          {credit > 0 && <Chip label={`Credit: ${formatCurrency(credit)}`} size="small" color="info" variant="outlined" />}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {registration && entries.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <LockStatusChip locked={registration.entriesLocked} />
            <InvoiceButton
              rowIndex={registration.rowIndex}
              type="entries"
              invoiceNumber={registration.entriesInvoiceNumber}
              printedAt={registration.entriesInvoicePrintedAt}
            />
          </Box>
        )}

        {unallocated > 0 && (
          <Typography variant="caption" color="text.secondary" display="block">
            Unallocated: {formatCurrency(unallocated)} — recorded as a whole-school lump sum, not tied to a specific entry or participant.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function StaffFeeCard({ staff, prices, onSaveRow, onBulkMarkPaid }) {
  const [bulkOpen, setBulkOpen] = useState(false);
  const rows = staff.map((s, i) => ({ ...s, owed: staffMemberFeeOwed(i, prices) }));
  const unpaidCount = rows.filter(s => (s.feeAmountPaid || 0) < s.owed).length;

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography fontWeight={600}>Staff / Chaperone Extra Fee</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          The school's first 2 teachers/chaperones are free — every one after owes a flat extra fee.
        </Typography>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No staff/chaperones registered for this school yet.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Owed</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount Paid / Paid By</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(s => (
                  <TableRow key={s.rowIndex} hover>
                    <TableCell>{s.firstName} {s.lastName}</TableCell>
                    <TableCell>{s.role || '—'}</TableCell>
                    <TableCell>{formatCurrency(s.owed)}</TableCell>
                    <TableCell>
                      <FeeAmountEditor
                        amountPaid={s.feeAmountPaid}
                        paidBy={s.feePaidBy}
                        owed={s.owed}
                        onSave={(amount, paidBy) => onSaveRow(s.id, { feeAmountPaid: amount, feePaidBy: paidBy })}
                      />
                    </TableCell>
                    <TableCell><StatusChip amountPaid={s.feeAmountPaid} amountOwed={s.owed} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      <BulkPaidDialog
        open={bulkOpen}
        label={`This will mark ${unpaidCount} currently-unpaid staff member${unpaidCount === 1 ? '' : 's'} as fully paid.`}
        onClose={() => setBulkOpen(false)}
        onConfirm={(paidBy) => onBulkMarkPaid(rows.filter(s => (s.feeAmountPaid || 0) < s.owed), paidBy)}
      />
    </Card>
  );
}

// Only positive per-category differences count toward "outstanding" — a
// refund owed in one category shouldn't visually cancel out owing in
// another in this one summary number.
function schoolOutstandingTotal(schoolName, { registrations, participants, entries, staff, prices, feePayments }) {
  const reg = registrations.find(r => r.schoolName === schoolName);
  const parts = participants.filter(p => p.schoolName === schoolName);
  const ents = entries.filter(e => e.schoolName === schoolName);
  const stf = staff.filter(s => s.schoolName === schoolName);
  const paidEntries = feePayments.filter(p => p.schoolName === schoolName && p.category === 'entry').reduce((sum, p) => sum + p.amount, 0);

  const schoolRegDiff = schoolRegFeeOwed(prices) - (reg?.schoolRegFeeAmountPaid || 0);
  const participationDiff = parts.reduce((sum, p) => sum + (participationFeeOwed(prices) - (p.participationFeeAmountPaid || 0)), 0);
  const entryDiff = schoolEntryFeeOwed(ents, prices) - paidEntries;
  const staffDiff = stf.reduce((sum, s, i) => sum + (staffMemberFeeOwed(i, prices) - (s.feeAmountPaid || 0)), 0);

  return [schoolRegDiff, participationDiff, entryDiff, staffDiff].reduce((sum, d) => sum + Math.max(0, d), 0);
}

export default function SchoolLookupSection() {
  const [registrations, setRegistrations] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [entries, setEntries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [prices, setPrices] = useState({});
  const [feePayments, setFeePayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [transactionsDialogOpen, setTransactionsDialogOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reg, part, ent, stf, prc, pay] = await Promise.all([
        adminClient.getEventRegistrations(),
        adminClient.getParticipants(),
        adminClient.getEntries(),
        adminClient.getStaff(),
        adminClient.getPrices(),
        adminClient.getFeePayments(),
      ]);
      setRegistrations(reg);
      setParticipants(part);
      setEntries(ent);
      setStaff(stf);
      // adminClient.getPrices() returns the Prices-tab row list (used by the
      // price-editing page), i.e. [{ rowIndex, item, price }] — flatten it
      // into the { [item]: price } map that entryRateFor/participationFeeOwed/
      // schoolRegFeeOwed expect (same shape /api/teacher/prices already
      // returns directly).
      setPrices(Object.fromEntries(prc.map(p => [p.item, p.price])));
      setFeePayments(pay);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const schoolNames = useMemo(() =>
    [...new Set(registrations.map(r => r.schoolName))].filter(Boolean).sort(),
    [registrations]
  );

  const schoolRegistration = useMemo(() =>
    registrations.find(r => r.schoolName === selectedSchool) || null,
    [registrations, selectedSchool]
  );
  const schoolParticipants = useMemo(() =>
    selectedSchool ? participants.filter(p => p.schoolName === selectedSchool) : [],
    [participants, selectedSchool]
  );
  const schoolEntries = useMemo(() =>
    selectedSchool ? entries.filter(e => e.schoolName === selectedSchool) : [],
    [entries, selectedSchool]
  );
  const schoolStaff = useMemo(() =>
    selectedSchool ? staff.filter(s => s.schoolName === selectedSchool) : [],
    [staff, selectedSchool]
  );
  const schoolFeePayments = useMemo(() =>
    selectedSchool ? feePayments.filter(p => p.schoolName === selectedSchool) : [],
    [feePayments, selectedSchool]
  );
  const schoolEntryFeePayments = useMemo(() =>
    schoolFeePayments.filter(p => p.category === 'entry'),
    [schoolFeePayments]
  );
  // Money owed back to the school from a deleted paid participant/entry, or
  // an entry left behind during a "replace dancer" — see schoolCreditBalance
  // and POST /api/teacher/dancers/:id/replace.
  const schoolCredit = useMemo(() => schoolCreditBalance(schoolFeePayments), [schoolFeePayments]);

  const outstandingTotal = selectedSchool
    ? schoolOutstandingTotal(selectedSchool, { registrations, participants, entries, staff, prices, feePayments })
    : 0;

  // Best-effort audit-trail write — a logging failure should never surface
  // as an error for the actual payment action it's describing (by the time
  // this is called, the real amount-paid update has already succeeded). See
  // FEE_PAYMENT_COLUMNS in sheetColumns.js: 'entry' rows are the source of
  // truth for Entry Fees' paid total; the other three categories log here
  // purely for display in the Transactions viewer, alongside their own
  // already-authoritative cumulative field.
  // Appends a locally-synthesized record straight into feePayments state on
  // success — rowIndex: -Date.now() as a collision-proof placeholder, since
  // these 3 categories never expose a delete action (only Entry Fees does),
  // so this value is never used to target a future write, just React keys
  // and display. Failing to log is never surfaced as an error for the
  // actual payment action it's describing — by the time this is called, the
  // real amount-paid update has already succeeded.
  const logFeePayment = async ({ category, amount, paidBy, note }) => {
    const payment = { schoolName: selectedSchool, category, amount, paidBy: paidBy || '', note: note || '' };
    try {
      await adminClient.addFeePayment(payment);
      setFeePayments(prev => [...prev, { ...payment, rowIndex: -Date.now(), timestamp: new Date().toISOString() }]);
    } catch (err) {
      console.warn('Failed to log transaction (the payment itself still succeeded):', err.message);
    }
  };

  // Records that `amount` of the school's credit balance was just applied
  // (see ApplyPaymentDialog's mode="credit") — a negative school-credit row,
  // so schoolCreditBalance's running sum decrements by exactly that much.
  // Refetches the full payments list rather than patching locally since the
  // actual fee application (handled by the dialog's normal onApply* calls)
  // already went through its own state updates independently.
  const handleCreditConsumed = async (amount) => {
    await adminClient.addFeePayment({
      schoolName: selectedSchool, category: 'school-credit', amount: -amount,
      paidBy: 'School credit', note: 'Applied toward outstanding fees',
    });
    const pay = await adminClient.getFeePayments();
    setFeePayments(pay);
  };

  // Registrations are only ever added/deleted through SchoolRegistrationsSection
  // (never by a teacher), so nothing else can shift a registration row's
  // position out from under this page — rowIndex-keyed local patching is safe.
  const handleSaveSchoolFee = async (rowIndex, fields, note) => {
    const prevPaid = registrations.find(r => r.rowIndex === rowIndex)?.schoolRegFeeAmountPaid || 0;
    await adminClient.updateSchoolFee(rowIndex, fields);
    if (fields.schoolRegFeeAmountPaid !== undefined) {
      const delta = fields.schoolRegFeeAmountPaid - prevPaid;
      if (delta > 0) await logFeePayment({ category: 'registration', amount: delta, paidBy: fields.schoolRegFeePaidBy, note });
    }
    setRegistrations(prev => prev.map(r => r.rowIndex === rowIndex ? { ...r, ...fields } : r));
  };

  // id: participantId, not rowIndex — a teacher can add/remove a dancer at
  // any moment from a different part of the app, which would shift row
  // positions out from under a long-open admin session. The backend now
  // resolves the target row fresh by this stable id at write time (see
  // PATCH /api/admin/participants/:participantId/fee), so addressing by id
  // here keeps the local patch below correct regardless.
  const handleSaveParticipantFee = async (id, fields) => {
    const prev = participants.find(p => p.id === id);
    const prevPaid = prev?.participationFeeAmountPaid || 0;
    await adminClient.updateParticipantFee(id, fields);
    if (fields.participationFeeAmountPaid !== undefined) {
      const delta = fields.participationFeeAmountPaid - prevPaid;
      if (delta > 0) {
        await logFeePayment({
          category: 'participant', amount: delta, paidBy: fields.participationFeePaidBy,
          note: prev ? `${prev.firstName} ${prev.lastName}`.trim() : '',
        });
      }
    }
    setParticipants(prev => prev.map(p => p.id === id ? { ...p, ...fields } : p));
  };

  // Applies one payment across the school's participants in list order
  // (registration order), fully paying each still-owing student in turn
  // until the amount runs out — the last student touched may end up only
  // partially paid if funds ran out mid-way. Returns any leftover that
  // couldn't be applied (everyone already fully paid).
  const handleApplyParticipationPayment = async (amount, paidBy, targetParticipants = schoolParticipants, note) => {
    const owed = participationFeeOwed(prices);
    let remaining = amount;
    const updates = [];
    for (const p of targetParticipants) {
      if (remaining <= 0) break;
      const alreadyPaid = p.participationFeeAmountPaid || 0;
      const stillOwed = owed - alreadyPaid;
      if (stillOwed <= 0) continue;
      const applied = Math.min(remaining, stillOwed);
      updates.push({ id: p.id, participationFeeAmountPaid: alreadyPaid + applied, participationFeePaidBy: paidBy });
      remaining -= applied;
    }
    await Promise.all(updates.map(u =>
      adminClient.updateParticipantFee(u.id, { participationFeeAmountPaid: u.participationFeeAmountPaid, participationFeePaidBy: u.participationFeePaidBy })
    ));
    const applied = amount - remaining;
    if (applied > 0) {
      await logFeePayment({
        category: 'participant', amount: applied, paidBy,
        note: combineNotes(note, `${updates.length} participant${updates.length === 1 ? '' : 's'}`),
      });
    }
    setParticipants(prev => prev.map(p => {
      const u = updates.find(u => u.id === p.id);
      return u ? { ...p, participationFeeAmountPaid: u.participationFeeAmountPaid, participationFeePaidBy: u.participationFeePaidBy } : p;
    }));
    return remaining;
  };

  // Same lump-sum-in-order distribution as handleApplyParticipationPayment,
  // but over this school's staff/chaperones and their per-index owed
  // amount — addressed by staffId for the same reason participants are.
  const handleApplyExtraFee = async (amount, paidBy, note) => {
    let remaining = amount;
    const updates = [];
    schoolStaff.forEach((s, i) => {
      if (remaining <= 0) return;
      const owed = staffMemberFeeOwed(i, prices);
      const alreadyPaid = s.feeAmountPaid || 0;
      const stillOwed = owed - alreadyPaid;
      if (stillOwed <= 0) return;
      const applied = Math.min(remaining, stillOwed);
      updates.push({ id: s.id, feeAmountPaid: alreadyPaid + applied, feePaidBy: paidBy });
      remaining -= applied;
    });
    await Promise.all(updates.map(u =>
      adminClient.updateStaffFee(u.id, { feeAmountPaid: u.feeAmountPaid, feePaidBy: u.feePaidBy })
    ));
    const applied = amount - remaining;
    if (applied > 0) {
      await logFeePayment({
        category: 'extra', amount: applied, paidBy,
        note: combineNotes(note, `${updates.length} staff/chaperone${updates.length === 1 ? '' : 's'}`),
      });
    }
    setStaff(prev => prev.map(s => {
      const u = updates.find(u => u.id === s.id);
      return u ? { ...s, feeAmountPaid: u.feeAmountPaid, feePaidBy: u.feePaidBy } : s;
    }));
    return remaining;
  };

  const handleApplyRegistrationFee = async (amount, paidBy, note) => {
    const paid = schoolRegistration?.schoolRegFeeAmountPaid || 0;
    await handleSaveSchoolFee(schoolRegistration.rowIndex, {
      schoolRegFeeAmountPaid: paid + amount,
      schoolRegFeePaidBy: paidBy || schoolRegistration?.schoolRegFeePaidBy || '',
    }, note);
  };

  // id: staffId — see handleSaveParticipantFee for why.
  const handleSaveStaffFee = async (id, fields) => {
    const prev = staff.find(s => s.id === id);
    const prevPaid = prev?.feeAmountPaid || 0;
    await adminClient.updateStaffFee(id, fields);
    if (fields.feeAmountPaid !== undefined) {
      const delta = fields.feeAmountPaid - prevPaid;
      if (delta > 0) {
        await logFeePayment({
          category: 'extra', amount: delta, paidBy: fields.feePaidBy,
          note: prev ? `${prev.firstName} ${prev.lastName}`.trim() : '',
        });
      }
    }
    setStaff(prev => prev.map(s => s.id === id ? { ...s, ...fields } : s));
  };

  const handleBulkMarkStaffPaid = async (unpaidRows, paidBy) => {
    await Promise.all(unpaidRows.map(s =>
      adminClient.updateStaffFee(s.id, { feeAmountPaid: s.owed, feePaidBy: paidBy })
    ));
    const totalApplied = unpaidRows.reduce((sum, s) => sum + (s.owed - (s.feeAmountPaid || 0)), 0);
    if (totalApplied > 0) {
      await logFeePayment({ category: 'extra', amount: totalApplied, paidBy, note: `Marked ${unpaidRows.length} staff fully paid` });
    }
    setStaff(prev => prev.map(s => {
      const u = unpaidRows.find(u => u.id === s.id);
      return u ? { ...s, feeAmountPaid: u.owed, feePaidBy: paidBy } : s;
    }));
  };

  // Fee Payments rows have no natural stable ID (an anonymous append-only
  // ledger) and adding/deleting one shifts every row below it — unlike the
  // edit-only handlers above, these two do need a fresh read afterward, but
  // only of this one list, not the full fetchAll().
  const handleAddEntryPayment = async (payment) => {
    await adminClient.addFeePayment({ ...payment, category: 'entry' });
    const pay = await adminClient.getFeePayments();
    setFeePayments(pay);
  };

  // "By participant" Entry Fee payment — resolves selected dancers strictly
  // one at a time, in the order checked. For each, the *shared* remaining
  // pool cascades through: (1) their entries, fully paying each still-owing
  // one in turn; (2) their Participation Fee, if anything's left (reuses
  // handleApplyParticipationPayment scoped to just this one dancer); (3) a
  // 'credit' ledger row for whatever's still left once neither applies —
  // this dancer has nothing else we track that they could owe. Only once a
  // participant is fully resolved (including any credit) does the pool move
  // to the next selected participant — so if you're splitting one payment
  // across several dancers' entries specifically, apply it to each
  // separately instead of selecting them all here.
  const handleApplyEntryFeeByParticipant = async (amount, paidBy, selectedParticipantIds, note) => {
    let remaining = amount;
    const selected = schoolParticipants.filter(p => selectedParticipantIds.has(p.id));

    for (const participant of selected) {
      if (remaining <= 0) break;

      const theirEntries = schoolEntries.filter(e => (e.dancerIds || []).includes(participant.id));
      for (const entry of theirEntries) {
        if (remaining <= 0) break;
        const rate = entryRateFor(entry, prices);
        const alreadyPaid = paidForEntryAndParticipant(entry.title, participant.id, schoolEntryFeePayments);
        const stillOwed = rate - alreadyPaid;
        if (stillOwed <= 0) continue;
        const applied = Math.min(remaining, stillOwed);
        await adminClient.addFeePayment({
          schoolName: selectedSchool, category: 'entry', entryTitle: entry.title, participantId: participant.id,
          amount: applied, paidBy, note: combineNotes(note, entry.title),
        });
        remaining -= applied;
      }

      if (remaining > 0) {
        remaining = await handleApplyParticipationPayment(remaining, paidBy, [participant], note);
      }

      if (remaining > 0) {
        await adminClient.addFeePayment({
          schoolName: selectedSchool, category: 'credit', participantId: participant.id,
          amount: remaining, paidBy, note: combineNotes(note, 'No outstanding fees — recorded as credit'),
        });
        remaining = 0;
      }
    }

    const pay = await adminClient.getFeePayments();
    setFeePayments(pay);
    return remaining;
  };

  // Builds the 4-category owed/paid/balance breakdown for the Final Invoice
  // — reusing the exact same formulas already powering this page (rather
  // than re-deriving them server-side) — and opens the PDF. The backend just
  // lays these numbers out; see generateFinalInvoicePdf.
  const handleOpenFinalInvoice = () => {
    if (!schoolRegistration) return;

    const regOwed = schoolRegFeeOwed(prices);
    const regPaid = schoolRegistration.schoolRegFeeAmountPaid || 0;

    const participantOwedEach = participationFeeOwed(prices);
    const participantOwed = schoolParticipants.length * participantOwedEach;
    const participantPaid = schoolParticipants.reduce((sum, p) => sum + (p.participationFeeAmountPaid || 0), 0);

    const entryOwed = schoolEntryFeeOwed(schoolEntries, prices);
    const entryPaid = schoolEntryFeePayments.reduce((sum, p) => sum + p.amount, 0);

    const extraOwed = schoolStaff.reduce((sum, s, i) => sum + staffMemberFeeOwed(i, prices), 0);
    const extraPaid = schoolStaff.reduce((sum, s) => sum + (s.feeAmountPaid || 0), 0);

    const items = [
      { label: 'School Registration Fee', owed: regOwed, paid: regPaid, balance: regOwed - regPaid },
      { label: 'Participation Fees', owed: participantOwed, paid: participantPaid, balance: participantOwed - participantPaid },
      { label: 'Entry Fees', owed: entryOwed, paid: entryPaid, balance: entryOwed - entryPaid },
      { label: 'Extra Fee (Staff/Chaperone)', owed: extraOwed, paid: extraPaid, balance: extraOwed - extraPaid },
    ];

    adminClient.openInvoicePdf(schoolRegistration.rowIndex, 'final', { items: JSON.stringify(items) });
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Box>

        {/* School picker — a transient search dropdown rather than a
            permanently-reserved list, so the payment cards below get the
            full page width once a school is picked. */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
          <Autocomplete
            size="small"
            fullWidth
            options={schoolNames}
            value={selectedSchool}
            onChange={(_, val) => setSelectedSchool(val)}
            noOptionsText={registrations.length === 0 ? 'No registrations yet.' : 'No matching schools.'}
            renderOption={(props, name) => {
              const total = schoolOutstandingTotal(name, { registrations, participants, entries, staff, prices, feePayments });
              const paid = total === 0;
              return (
                <Box component="li" {...props} key={name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2" noWrap>{name}</Typography>
                  <Chip
                    label={paid ? 'Paid' : `Owing ${formatCurrency(total)}`}
                    color={paid ? 'success' : 'warning'}
                    size="small"
                    variant={paid ? 'filled' : 'outlined'}
                    sx={{ flexShrink: 0 }}
                  />
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search schools..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
              />
            )}
          />
          <Tooltip title="Refresh — pulls in any changes made elsewhere (e.g. by a teacher) since this page loaded">
            <IconButton onClick={fetchAll} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          {participants.filter(p => !p.isTest).length} participants · {staff.filter(s => !s.isTest).length} teachers/chaperones (all schools, test schools excluded)
        </Typography>

        {/* Detail panel */}
        <Box>
          {!selectedSchool ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280, color: 'text.secondary' }}>
              <Typography variant="body2">Select a school to view its payment details</Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>{selectedSchool}{schoolRegistration?.isTest && <TestSchoolChip />}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {schoolParticipants.length} participants · {schoolStaff.length} teachers/chaperones
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600} color={outstandingTotal > 0 ? 'warning.main' : 'success.main'} sx={{ mt: 0.5 }}>
                    Total outstanding: {formatCurrency(outstandingTotal)}
                  </Typography>
                  {schoolCredit > 0 && (
                    <Typography variant="body2" fontWeight={600} color="info.main" sx={{ mt: 0.25 }}>
                      School credit available: {formatCurrency(schoolCredit)}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {schoolCredit > 0 && (
                    <Button variant="outlined" color="info" startIcon={<PaidIcon />} onClick={() => setCreditDialogOpen(true)}>
                      Apply Credit
                    </Button>
                  )}
                  <Tooltip title="View all transactions">
                    <IconButton onClick={() => setTransactionsDialogOpen(true)}>
                      <HistoryIcon />
                    </IconButton>
                  </Tooltip>
                  <Button variant="contained" startIcon={<PaidIcon />} onClick={() => setApplyDialogOpen(true)}>
                    Apply Payment
                  </Button>
                </Box>
              </Box>

              <TransactionsDialog
                open={transactionsDialogOpen}
                onClose={() => setTransactionsDialogOpen(false)}
                schoolName={selectedSchool}
                payments={schoolFeePayments}
              />

              <ApplyPaymentDialog
                open={applyDialogOpen}
                onClose={() => setApplyDialogOpen(false)}
                schoolName={selectedSchool}
                registration={schoolRegistration}
                participants={schoolParticipants}
                entries={schoolEntries}
                staff={schoolStaff}
                entryPayments={schoolEntryFeePayments}
                prices={prices}
                onApplyRegistrationFee={handleApplyRegistrationFee}
                onApplyParticipantFee={handleApplyParticipationPayment}
                onApplyEntryFee={(amount, paidBy, selectedParticipantIds, note) =>
                  selectedParticipantIds && selectedParticipantIds.size > 0
                    ? handleApplyEntryFeeByParticipant(amount, paidBy, selectedParticipantIds, note)
                    : handleAddEntryPayment({ schoolName: selectedSchool, amount, paidBy, note }).then(() => 0)
                }
                onApplyExtraFee={handleApplyExtraFee}
              />

              <ApplyPaymentDialog
                open={creditDialogOpen}
                onClose={() => setCreditDialogOpen(false)}
                schoolName={selectedSchool}
                registration={schoolRegistration}
                participants={schoolParticipants}
                entries={schoolEntries}
                staff={schoolStaff}
                entryPayments={schoolEntryFeePayments}
                prices={prices}
                onApplyRegistrationFee={handleApplyRegistrationFee}
                onApplyParticipantFee={handleApplyParticipationPayment}
                onApplyEntryFee={(amount, paidBy, selectedParticipantIds, note) =>
                  selectedParticipantIds && selectedParticipantIds.size > 0
                    ? handleApplyEntryFeeByParticipant(amount, paidBy, selectedParticipantIds, note)
                    : handleAddEntryPayment({ schoolName: selectedSchool, amount, paidBy, note }).then(() => 0)
                }
                onApplyExtraFee={handleApplyExtraFee}
                mode="credit"
                maxAmount={schoolCredit}
                onCreditConsumed={handleCreditConsumed}
              />

              <SchoolRegFeeCard registration={schoolRegistration} prices={prices} onSave={handleSaveSchoolFee} />

              <ParticipationFeesCard
                participants={schoolParticipants}
                prices={prices}
                onSaveRow={handleSaveParticipantFee}
                onApplyPayment={handleApplyParticipationPayment}
                registration={schoolRegistration}
              />

              <EntryFeesCard
                entries={schoolEntries}
                participants={schoolParticipants}
                prices={prices}
                payments={schoolEntryFeePayments}
                registration={schoolRegistration}
              />

              <StaffFeeCard
                staff={schoolStaff}
                prices={prices}
                onSaveRow={handleSaveStaffFee}
                onBulkMarkPaid={handleBulkMarkStaffPaid}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Tooltip title={schoolRegistration ? '' : 'No event registration record for this school yet'}>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<ReceiptLongIcon />}
                      onClick={handleOpenFinalInvoice}
                      disabled={!schoolRegistration}
                    >
                      Final Invoice
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Stack>
          )}
        </Box>
      </Box>
    </>
  );
}
